// @flow
import React from 'react';
import Latex from './Latex.js';
import color from './color.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import { figureSizes } from './designSystem.js';

type Props = {|
  /** The width (in pixels) for the plot */
  width: number,
  /** The height (in pixels) for the plot */
  height: number,
  axisTitle: string,
  axisMin: number,
  axisMax: number,
  axisMajorTickInterval: number,
  axisMinorTickInterval: number,
  min: number,
  q1: number,
  median: number,
  q3: number,
  max: number,
  outliers?: number[],
  // There are certain cases where we don't want <Figure> to
  // apply its default margins. At time of writing, SolidifyFigureGrid
  // and SolidifyAnnotatedFigure both require this. I'm not really happy
  // with this approach though, so need to rethink it sometime.
  _removeFigureMargin?: boolean,
  ...CompilerInjectedProps,
|};

const BoxAndWhiskerPlot = (props: Props) => {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    BoxAndWhiskerPlot,
  );

  // Ad-hoc assertion: ensure that the dimensions are one of the allowed figure sizes from our design system.
  React.useEffect(() => {
    if (!props._performValidation) return;
    const { width, height } = props;
    if (
      figureSizes.find((s) => s.width === width && s.height === height) !==
      undefined
    )
      return;

    const allowedSizesMsg = figureSizes
      .map((s) => `- ${s.width}x${s.height}`)
      .join('\n');
    addErrors([
      {
        message: [
          `BoxAndWhiskerPlot cannot be ${width}x${height}.`,
          `You must use one of the following dimensions:`,
          allowedSizesMsg,
        ].join('\n'),
        ...props._sourceLocations?.BoxAndWhiskerPlot,
      },
    ]);
  });

  if (failedValidation) return null;

  const {
    width,
    height,
    axisTitle,
    axisMin,
    axisMax,
    axisMajorTickInterval,
    axisMinorTickInterval,
    min,
    q1,
    median,
    q3,
    max,
    outliers = [],
  } = props;

  // TODO move this into the spec system.
  if (axisMajorTickInterval % axisMinorTickInterval !== 0) {
    throw new Error(
      'axisMinorTickInterval should be a divisor of axisMajorTickInterval',
    );
  }

  // TODO support specificying precision if decimalPlaces fails due to floating point representation issues?
  const formatTick = (x) => {
    // debugger;
    const result = x.toFixed(decimalPlaces(axisMajorTickInterval));
    return result;
  };
  const scale = (x) => {
    const rebased = x - axisMin;
    const ratio = width / (axisMax - axisMin);
    return rebased * ratio;
  };
  const centered = (container, box) => (container - box) / 2;

  const boxHeight = height / 4;
  const whiskerHeight = boxHeight / 2;
  const majorTickLength = 20;
  const minorTickLength = 10;
  const strokeColor = color.ebony;
  const textColor = color.ebony;
  const lineProps = { stroke: strokeColor, strokeLinecap: 'square' };

  return (
    <Figure _removeFigureMargin={props._removeFigureMargin}>
      <div style={{ display: 'inline-block', position: 'relative' }}>
        {/* Plot title */}
        {axisTitle !== '' && (
          // We probably shouldn't have allowed the empty string.
          // But here we are, so we shouldn't take up layout space
          // if an empty string has been supplied.
          <div
            style={{
              textAlign: 'center',
              marginBottom: 10,
              fontWeight: 'bold',
              color: textColor,
            }}
          >
            {axisTitle}
          </div>
        )}
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: 'block', border: `1px solid ${strokeColor}` }}
        >
          {/* Left whisker */}
          <line
            x1={scale(min)}
            y1={centered(height, whiskerHeight)}
            x2={scale(min)}
            y2={centered(height, whiskerHeight) + whiskerHeight}
            {...lineProps}
          />
          <line
            x1={scale(min)}
            y1={height / 2}
            x2={scale(q1)}
            y2={height / 2}
            {...lineProps}
          />

          {/* Inter-quartile-range box */}
          <line
            x1={scale(q1)}
            y1={centered(height, boxHeight)}
            x2={scale(q1)}
            y2={centered(height, boxHeight) + boxHeight}
            {...lineProps}
          />
          <line
            x1={scale(median)}
            y1={centered(height, boxHeight)}
            x2={scale(median)}
            y2={centered(height, boxHeight) + boxHeight}
            {...lineProps}
          />
          <line
            x1={scale(q3)}
            y1={centered(height, boxHeight)}
            x2={scale(q3)}
            y2={centered(height, boxHeight) + boxHeight}
            {...lineProps}
          />
          <line
            x1={scale(q1)}
            y1={centered(height, boxHeight)}
            x2={scale(q3)}
            y2={centered(height, boxHeight)}
            {...lineProps}
          />
          <line
            x1={scale(q1)}
            y1={centered(height, boxHeight) + boxHeight}
            x2={scale(q3)}
            y2={centered(height, boxHeight) + boxHeight}
            {...lineProps}
          />

          {/* Right whisker */}
          <line
            x1={scale(q3)}
            y1={height / 2}
            x2={scale(max)}
            y2={height / 2}
            {...lineProps}
          />
          <line
            x1={scale(max)}
            y1={centered(height, whiskerHeight)}
            x2={scale(max)}
            y2={centered(height, whiskerHeight) + whiskerHeight}
            {...lineProps}
          />

          {/* Outliers */}
          {outliers.map((outlier) => (
            <circle
              key={outlier}
              cx={scale(outlier)}
              cy={height / 2}
              r={2}
              stroke={strokeColor}
              fill="none"
            />
          ))}

          {/* Axis ticks */}
          {range(axisMin, axisMax, axisMinorTickInterval)
            .slice(1, -1)
            .map((tickX) => (
              <line
                key={tickX}
                x1={scale(tickX)}
                y1={height}
                x2={scale(tickX)}
                y2={height - minorTickLength}
                stroke={strokeColor}
                strokeLinecap="square"
              />
            ))}
          {range(axisMin, axisMax, axisMajorTickInterval)
            .slice(1, -1)
            .map((tickX) => (
              <line
                key={tickX}
                x1={scale(tickX)}
                y1={height}
                x2={scale(tickX)}
                y2={height - majorTickLength}
                stroke={strokeColor}
                strokeLinecap="square"
              />
            ))}
        </svg>
        {/* Major tick labels */}
        {range(axisMin, axisMax, axisMajorTickInterval).map((tickX) => (
          <div
            key={tickX}
            style={{
              position: 'absolute',
              left: scale(tickX),
              transform: 'translateX(-50%)',
              marginTop: 5,
              userSelect: 'none',
              color: textColor,
              fontSize: 12,
            }}
          >
            <Latex expression={formatTick(tickX)} />
          </div>
        ))}
        {/* Spacer equal in size to the Major tick labels height (since they aren't in document flow).
          Bit of a hack, but no time to think of a more robust solution atm. */}
        <div style={{ height: 25 }} />
      </div>
    </Figure>
  );
};
BoxAndWhiskerPlot.spec = {
  width: t.number,
  height: t.number,
  axisTitle: t.string,
  axisMin: t.number,
  axisMax: t.number,
  axisMajorTickInterval: t.number,
  axisMinorTickInterval: t.number,
  min: t.number,
  q1: t.number,
  median: t.number,
  q3: t.number,
  max: t.number,
  outliers: t.optional(t.arrayOf(t.number)),
};

function range(min, max, interval) {
  // NOTE: we intentionally avoid stepping through a loop by going += interval
  // so as to avoid floating point imprecisions that arises from summing
  // imprecise representations.
  const result = [];
  const intervals = Math.round((max - min) / interval) + 1; // +1 for end bound inclusive interval
  for (let i = 0; i < intervals; i++) result.push(min + i * interval);
  return result;
}

function decimalPlaces(n) {
  // NOTE Will fail on scientific notation (ie. '1.123e+50')
  const match = String(n).match(/[0-9]+\.([0-9]+)/);
  if (match === null) return 0;
  return match[1].length;
}

export default BoxAndWhiskerPlot;
